import { NetworkType, getSupportedNetworkTypeFromChainId } from 'abis/set/sets'
import { ZERO_EX_AFFILIATE_AVAX_ADDRESS } from 'app-constants'
import Big from 'big.js'
import type { WithInitialValue } from 'components/widgets/invest-component/interfaces/common'
import type { CurrencyMetadata, ProductMetadata } from 'indices-list'
import type { PrimitiveAtom } from 'jotai'
import type { atomsWithQuery } from 'jotai-tanstack-query'
import { CAI } from 'products'
import { avalancheCurrencies, formatWeiToAmount } from 'utils'
import { type Address, type Hex, zeroAddress } from 'viem'
import type { RawQuotesResponse } from '../output/model'

export type QuoteParams = {
  sellCurrency: CurrencyMetadata
  buyCurrency: CurrencyMetadata
  sellAmount: Big
  enableSlippageProtection: boolean
  affiliateAddress: Address
  slippagePercentage: number
  chainId: NetworkType
  account: Address
  productAddress: Address
}

export const defaultCurrency: CurrencyMetadata = avalancheCurrencies.USDC

export const defaultQuoteResponse: QuotesResponse = {
  chainId: getSupportedNetworkTypeFromChainId(NetworkType.Avalanche),
  currency: defaultCurrency,
  buyAmount: Big(0),
  sellAmount: BigInt(0),
  gas: BigInt(0), //TODO: default gas price amount
  data: '0x' as Hex,
  to: zeroAddress,
  value: BigInt(0),
}

export const defaultQuoteParams: QuoteParams = {
  sellCurrency: defaultCurrency,
  buyCurrency: defaultCurrency,
  sellAmount: Big(0),
  enableSlippageProtection: true,
  affiliateAddress: ZERO_EX_AFFILIATE_AVAX_ADDRESS,
  slippagePercentage: 0.05, // TODO: take it from data-model after settings impl
  chainId: getSupportedNetworkTypeFromChainId(NetworkType.Avalanche),
  account: zeroAddress,
  productAddress: CAI.address,
}

export const addressFromCurrency = (currency: CurrencyMetadata) =>
  currency.address === zeroAddress ? currency.symbol : currency.address

export type QuotesResponse = {
  chainId: NetworkType
  currency: CurrencyMetadata
  buyAmount: Big
  sellAmount: bigint
  gas: bigint
  data: Address
  to: Address
  value?: bigint
}

type QueryReturn = ReturnType<typeof atomsWithQuery<QuotesResponse>>

export type QueryQuotesRequest = QueryReturn['0']
export type QueryQuotesDispatch = QueryReturn['1']

export type QuotesRequestAtoms = {
  params: PrimitiveAtom<QuoteParams> & WithInitialValue<QuoteParams>
  response: QueryQuotesRequest
  dispatch: QueryQuotesDispatch
}

export const parseQuoteResponse = (
  data: RawQuotesResponse,
  params: QuoteParams,
): QuotesResponse => {
  return {
    chainId: params.chainId,
    currency: params.sellCurrency,
    buyAmount: formatWeiToAmount(data.buyAmount, params.buyCurrency.decimals),
    sellAmount: BigInt(data.sellAmount),
    gas: BigInt(data.transaction.gas ?? 0), //TODO: default gas price amount
    data: data.transaction.data as Address,
    to: data.transaction.to as Address,
    value: BigInt(data.transaction.value),
  }
}

export const getInitialQuotesParams = (
  params: ProductMetadata,
  inputCurrency: CurrencyMetadata,
  account: Address,
): QuoteParams => {
  return {
    ...defaultQuoteParams,
    sellCurrency: inputCurrency,
    buyCurrency: params.productCurrency,
    sellAmount: Big(0),
    slippagePercentage: params.defaultSlippagePercentage,
    chainId: getSupportedNetworkTypeFromChainId(params.network),
    productAddress: params.address,
    account,
  }
}
